import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { appPathAfterLogin, csrfFromFlow, firstFlowMessage } from "../auth/kratosFlow.js";
import { useProductMe } from "../hooks/useProductMe.js";

function AuthShell({ children, subtitle }) {
  return (
    <div
      className="relative min-h-screen flex flex-col items-center justify-center px-4"
      style={{ backgroundColor: "var(--bg-base)", color: "var(--text-primary)" }}
    >
      <div
        className="pointer-events-none fixed inset-0 z-0"
        style={{
          opacity: 0.3,
          backgroundImage: `
            linear-gradient(var(--border) 1px, transparent 1px),
            linear-gradient(90deg, var(--border) 1px, transparent 1px)
          `,
          backgroundSize: "32px 32px",
        }}
        aria-hidden
      />
      <div className="relative z-[1] w-full max-w-[400px] flex flex-col items-center">
        <div className="self-start mb-6" style={{ opacity: 0.9 }}>
          <span style={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.14em", color: "var(--accent)" }}>
            idaxis
          </span>
        </div>
        <h1
          style={{
            fontSize: "24px",
            fontWeight: 700,
            letterSpacing: "0.2em",
            color: "var(--accent)",
            margin: 0,
            textAlign: "center",
          }}
        >
          IDWIZ
        </h1>
        <p className="text-secondary" style={{ fontSize: "14px", textAlign: "center", marginTop: "8px" }}>
          {subtitle}
        </p>
        <div style={{ height: "28px" }} />
        {children}
        <p className="text-dim" style={{ fontSize: "11px", textAlign: "center", marginTop: "24px" }}>
          idwiz by idaxis
        </p>
      </div>
    </div>
  );
}

function KratosLoginForm({ onLoginSuccess }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh } = useProductMe();
  const [flow, setFlow] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [initError, setInitError] = useState("");

  const loadFlow = useCallback(async () => {
    setInitError("");
    try {
      const res = await fetch("/api/auth/login/init", { credentials: "include" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setInitError(data?.error?.message || data?.error || "Could not start sign-in");
        return;
      }
      setFlow(data);
    } catch {
      setInitError("Could not reach the sign-in service");
    }
  }, []);

  useEffect(() => {
    loadFlow();
  }, [loadFlow]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!flow?.id) return;
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch("/api/auth/login/submit", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          flow_id: flow.id,
          csrf_token: csrfFromFlow(flow),
          identifier: email.trim(),
          password,
        }),
      });
      const data = await res.json().catch(() => ({}));

      if (data?.redirect_browser_to) {
        window.location.assign(appPathAfterLogin(data));
        return;
      }

      if (!res.ok) {
        const msg =
          firstFlowMessage(data) ||
          data?.ui?.messages?.[0]?.text ||
          data?.error?.message ||
          "Sign-in failed. Check your email and password.";
        setError(String(msg));
        if (data?.id) setFlow(data);
        else await loadFlow();
        return;
      }

      const syncMe = onLoginSuccess ?? refresh;
      const me = await syncMe();
      const dest = appPathAfterLogin(data);
      if (me?.authenticated) {
        if (location.pathname === dest.split("?")[0] && !location.search) return;
        navigate(dest, { replace: true });
        return;
      }
      window.location.assign(dest);
    } catch {
      setError("Sign-in request failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell subtitle="Identity assessment for Okta">
      <div
        className="w-full"
        style={{
          background: "var(--bg-surface)",
          border: "1px solid var(--border-bright)",
          borderRadius: "8px",
          padding: "32px",
        }}
      >
        <h2 style={{ fontSize: "16px", fontWeight: 600, margin: "0 0 8px 0" }}>Sign in</h2>
        <p className="text-secondary" style={{ fontSize: "13px", margin: "0 0 20px 0" }}>
          Use your idwiz account credentials.
        </p>
        {initError ? (
          <p style={{ color: "var(--danger, #f87171)", fontSize: "13px", marginBottom: "12px" }}>{initError}</p>
        ) : null}
        <form onSubmit={onSubmit}>
          <label className="block text-secondary" style={{ fontSize: "12px", marginBottom: "6px" }}>
            Email
          </label>
          <input
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(ev) => setEmail(ev.target.value)}
            className="w-full"
            style={{
              marginBottom: "14px",
              padding: "10px 12px",
              borderRadius: "6px",
              border: "1px solid var(--border-bright)",
              background: "var(--bg-base)",
              color: "var(--text-primary)",
              fontSize: "14px",
            }}
          />
          <label className="block text-secondary" style={{ fontSize: "12px", marginBottom: "6px" }}>
            Password
          </label>
          <input
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(ev) => setPassword(ev.target.value)}
            className="w-full"
            style={{
              marginBottom: "8px",
              padding: "10px 12px",
              borderRadius: "6px",
              border: "1px solid var(--border-bright)",
              background: "var(--bg-base)",
              color: "var(--text-primary)",
              fontSize: "14px",
            }}
          />
          <div style={{ marginBottom: "16px", textAlign: "right" }}>
            <Link to="/recovery" style={{ fontSize: "12px", color: "var(--accent)", textDecoration: "none" }}>
              Forgot password?
            </Link>
          </div>
          {error ? (
            <p style={{ color: "var(--danger, #f87171)", fontSize: "13px", marginBottom: "12px" }}>{error}</p>
          ) : null}
          <button
            type="submit"
            disabled={submitting || !flow?.id || Boolean(initError)}
            className="w-full font-semibold"
            style={{
              height: "44px",
              borderRadius: "6px",
              border: "none",
              background: "var(--accent)",
              color: "#0a0f1a",
              cursor: submitting ? "wait" : "pointer",
              opacity: submitting || !flow?.id ? 0.7 : 1,
              fontSize: "15px",
            }}
          >
            {submitting ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    </AuthShell>
  );
}

function LoginLoadingShell() {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        background: "var(--bg-base)",
        color: "var(--text-secondary)",
      }}
    >
      Loading…
    </div>
  );
}

/** B2-UX-3: redirect to home when a valid Kratos session already exists. */
export default function Login({ onLoginSuccess }) {
  const { me, loading } = useProductMe();

  if (loading) {
    return <LoginLoadingShell />;
  }

  if (me?.authenticated) {
    return <Navigate to="/" replace />;
  }

  return <KratosLoginForm onLoginSuccess={onLoginSuccess} />;
}
